import React, { createContext, useContext, useReducer, useCallback, useRef, ReactNode, useMemo } from 'react';
import { type BlockData, type WordData } from '../config/wordOverlayConfig';
import { createBookDataService } from '../services/BookDataService';
import { adjustIdIfCollision, isAdjustedId, extractOriginalIdInfo, findNthOccurrence, getNextAvailableId } from '../utils/idCollisionHandler';

// === State Types ===
interface WordDataState {
  // Active spread (not single page)
  activeSpread: {
    leftPageIndex: number | null;
    rightPageIndex: number | null;
  };
  
  // Blocks stored by page index
  blocksByPage: Map<number, BlockData[]>;
  
  // Word data stored by page index
  wordDataByPage: Map<number, any>;
  
  // Loading state per page
  loadingPages: Set<number>;
  
  // Global error (can be enhanced to be per-page later)
  error: string | null;
  
  // Selection state (for visual feedback only)
  selectedWord: { wordId: string; pageIndex: number } | null;
  flashingWord: { wordId: string; pageIndex: number } | null; // For flashing effect when not in text edit mode
  highlightedWords: Set<string>;
  
  // Block highlighting for editing
  highlightedBlock: { blockId: number; boundingBox: { x: number; y: number; width: number; height: number } } | null;
  
  // UI state
  showWords: boolean;
  
  // Global edit mode (applies to all words/blocks)
  editMode: 'text' | 'resize' | 'move' | 'delete-word' | 'delete-block' | null;
  
  // Overlay recalculation trigger
  recalcTrigger: number;
}

// === Action Types ===
type WordDataAction =
  | { type: 'SET_ACTIVE_SPREAD'; payload: { leftPageIndex: number | null; rightPageIndex: number | null } }
  | { type: 'LOAD_PAGE_BLOCKS'; payload: { pageIndex: number; wordData: any; blocks: BlockData[] } }
  | { type: 'UPDATE_PAGE_BLOCKS'; payload: { pageIndex: number; blocks: BlockData[] } }
  | { type: 'SET_PAGE_LOADING'; payload: { pageIndex: number; loading: boolean } }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SELECTED_WORD'; payload: { wordId: string; pageIndex: number } | null }
  | { type: 'SET_FLASHING_WORD'; payload: { wordId: string; pageIndex: number } | null }
  | { type: 'TOGGLE_WORD_HIGHLIGHT'; payload: string }
  | { type: 'CLEAR_WORD_HIGHLIGHTS' }
  | { type: 'SET_HIGHLIGHTED_BLOCK'; payload: { blockId: number; boundingBox: { x: number; y: number; width: number; height: number } } | null }
  | { type: 'SET_SHOW_WORDS'; payload: boolean }
  | { type: 'SET_EDIT_MODE'; payload: 'text' | 'resize' | 'move' | 'delete-word' | 'delete-block' | null }
  | { type: 'DELETE_BLOCK'; payload: { pageIndex: number; blockId: number } }
  | { type: 'UPDATE_WORD_TEXT'; payload: { pageIndex: number; blockId: number; wordId: string | number; text: string } }
  | { type: 'UPDATE_WORD_POSITION'; payload: { pageIndex: number; blockId: number; wordId: string | number; boundingBox: { x: number; y: number; width: number; height: number } } }
  | { type: 'ADD_WORD_TO_BLOCK'; payload: { pageIndex: number; blockId: number; text: string; boundingBox: { x: number; y: number; width: number; height: number } } }
  | { type: 'REORDER_WORD_IN_BLOCK'; payload: { pageIndex: number; blockId: number; wordId: string | number; direction: 'up' | 'down' } }
  | { type: 'DELETE_WORD_FROM_BLOCK'; payload: { pageIndex: number; blockId: number; wordId: string | number } }
  | { type: 'UPDATE_BLOCK_METADATA'; payload: { pageIndex: number; blockId: number; metadata: Partial<BlockData> } }
  | { type: 'RECALCULATE_OVERLAYS' }
  | { type: 'CLEAR_SELECTION' }
  | { type: 'RESET_STATE' };

// === Initial State ===
const initialState: WordDataState = {
  activeSpread: {
    leftPageIndex: null,
    rightPageIndex: null,
  },
  blocksByPage: new Map(),
  wordDataByPage: new Map(),
  loadingPages: new Set(),
  error: null,
  selectedWord: null,
  flashingWord: null,
  highlightedWords: new Set(),
  highlightedBlock: null,
  showWords: false,
  editMode: null,
  recalcTrigger: 0,
};

// === Reducer ===
function wordDataReducer(state: WordDataState, action: WordDataAction): WordDataState {
  // Helper function to recalculate block bounding box from words
  const recalculateBlockBounds = (words: any[]): { x: number; y: number; width: number; height: number } | null => {
    if (words.length === 0) {
      return null;
    }
    
    let minX = words[0].boundingBox.x;
    let minY = words[0].boundingBox.y;
    let maxX = words[0].boundingBox.x + words[0].boundingBox.width;
    let maxY = words[0].boundingBox.y + words[0].boundingBox.height;
    
    for (const word of words) {
      minX = Math.min(minX, word.boundingBox.x);
      minY = Math.min(minY, word.boundingBox.y);
      maxX = Math.max(maxX, word.boundingBox.x + word.boundingBox.width);
      maxY = Math.max(maxY, word.boundingBox.y + word.boundingBox.height);
    }
    
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    };
  };

  switch (action.type) {
    case 'SET_ACTIVE_SPREAD':
      // Note: We don't clear loadedPagesRef here because pages might be reused
      // The ref tracks which pages have been loaded to prevent accidental reloads
      return {
        ...state,
        activeSpread: {
          leftPageIndex: action.payload.leftPageIndex,
          rightPageIndex: action.payload.rightPageIndex,
        },
        // Clear selection when spread changes
        selectedWord: null,
        highlightedWords: new Set(),
        highlightedBlock: null,
        editMode: null,
      };
      
    case 'LOAD_PAGE_BLOCKS':
      const newBlocksByPage = new Map(state.blocksByPage);
      const newWordDataByPage = new Map(state.wordDataByPage);
      newBlocksByPage.set(action.payload.pageIndex, action.payload.blocks);
      newWordDataByPage.set(action.payload.pageIndex, action.payload.wordData);
      
      const newLoadingPages = new Set(state.loadingPages);
      newLoadingPages.delete(action.payload.pageIndex);
      
      return {
        ...state,
        blocksByPage: newBlocksByPage,
        wordDataByPage: newWordDataByPage,
        loadingPages: newLoadingPages,
        error: null,
      };
      
    case 'UPDATE_PAGE_BLOCKS':
      const updatedBlocksByPage = new Map(state.blocksByPage);
      updatedBlocksByPage.set(action.payload.pageIndex, action.payload.blocks);
      return {
        ...state,
        blocksByPage: updatedBlocksByPage,
      };
      
    case 'SET_PAGE_LOADING':
      const newLoadingSet = new Set(state.loadingPages);
      if (action.payload.loading) {
        newLoadingSet.add(action.payload.pageIndex);
      } else {
        newLoadingSet.delete(action.payload.pageIndex);
      }
      return {
        ...state,
        loadingPages: newLoadingSet,
        error: action.payload.loading ? null : state.error,
      };
      
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      };
      
    case 'SET_SELECTED_WORD':
      console.log('[WordDataContext Reducer] SET_SELECTED_WORD:', action.payload);
      return {
        ...state,
        selectedWord: action.payload,
      };
      
    case 'SET_FLASHING_WORD':
      console.log('[WordDataContext Reducer] SET_FLASHING_WORD:', action.payload);
      return {
        ...state,
        flashingWord: action.payload,
      };
      
    case 'TOGGLE_WORD_HIGHLIGHT':
      const newHighlightedWords = new Set(state.highlightedWords);
      if (newHighlightedWords.has(action.payload)) {
        newHighlightedWords.delete(action.payload);
      } else {
        newHighlightedWords.add(action.payload);
      }
      return {
        ...state,
        highlightedWords: newHighlightedWords,
      };
      
    case 'SET_SHOW_WORDS':
      return {
        ...state,
        showWords: action.payload,
      };
      
    case 'SET_HIGHLIGHTED_BLOCK':
      return {
        ...state,
        highlightedBlock: action.payload,
      };
      
    case 'SET_EDIT_MODE':
      return {
        ...state,
        editMode: action.payload,
      };
      
    case 'DELETE_BLOCK':
      const { pageIndex: deleteBlockPageIndex, blockId: deleteBlockId } = action.payload;
      console.log('[Reducer] DELETE_BLOCK:', { deleteBlockPageIndex, deleteBlockId });
      const deleteBlockBlocks = state.blocksByPage.get(deleteBlockPageIndex) || [];
      console.log('[Reducer] Blocks before delete:', deleteBlockBlocks.length);
      const filteredBlocks = deleteBlockBlocks.filter(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        return blockIdNum !== deleteBlockId;
      });
      console.log('[Reducer] Blocks after delete:', filteredBlocks.length);
      
      const deleteBlockBlocksByPage = new Map(state.blocksByPage);
      deleteBlockBlocksByPage.set(deleteBlockPageIndex, filteredBlocks);
      
      return {
        ...state,
        blocksByPage: deleteBlockBlocksByPage,
      };
      
    case 'UPDATE_WORD_TEXT':
      const { pageIndex: textPageIndex, blockId, wordId, text } = action.payload;
      const textBlocks = state.blocksByPage.get(textPageIndex) || [];
      const updatedTextBlocks = textBlocks.map(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        if (blockIdNum === blockId) {
          const updatedWords = block.words.map(word =>
            word.wordId === wordId ? { ...word, text } : word
          );
          
          // Recalculate block bounding box after text change
          const newBlockBounds = recalculateBlockBounds(updatedWords);
          
          return {
            ...block,
            words: updatedWords,
            ...(newBlockBounds && { boundingBox: newBlockBounds })
          };
        }
        return block;
      });
      
      const updatedTextBlocksByPage = new Map(state.blocksByPage);
      updatedTextBlocksByPage.set(textPageIndex, updatedTextBlocks);
      
      return {
        ...state,
        blocksByPage: updatedTextBlocksByPage,
      };
      
    case 'UPDATE_WORD_POSITION':
      const { pageIndex: posPageIndex, blockId: posBlockId, wordId: posWordId, boundingBox } = action.payload;
      const posBlocks = state.blocksByPage.get(posPageIndex) || [];
      const updatedPosBlocks = posBlocks.map(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        if (blockIdNum === posBlockId) {
          const updatedWords = block.words.map(word => {
            if (word.wordId === posWordId) {
              // Preserve originalBoundingBox if it exists (for normalized values)
              const originalBoundingBox = (word as any).originalBoundingBox;
              return {
                ...word,
                boundingBox,
                ...(originalBoundingBox && { originalBoundingBox })
              };
            }
            return word;
          });
          
          // Recalculate block bounding box based on updated word positions
          const newBlockBounds = recalculateBlockBounds(updatedWords);
          
          return {
            ...block,
            words: updatedWords,
            ...(newBlockBounds && { boundingBox: newBlockBounds })
          };
        }
        return block;
      });
      
      const updatedPosBlocksByPage = new Map(state.blocksByPage);
      updatedPosBlocksByPage.set(posPageIndex, updatedPosBlocks);
      
      return {
        ...state,
        blocksByPage: updatedPosBlocksByPage,
      };
      
    case 'ADD_WORD_TO_BLOCK':
      const { pageIndex: addPageIndex, blockId: addWordBlockId, text: newWordText, boundingBox: newWordBoundingBox } = action.payload;
      const addBlocks = state.blocksByPage.get(addPageIndex) || [];
      const updatedAddBlocks = addBlocks.map(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        if (blockIdNum === addWordBlockId) {
          // Generate new wordId
          const maxWordId = block.words.length > 0 
            ? Math.max(...block.words.map(w => typeof w.wordId === 'string' ? parseInt(w.wordId) : w.wordId)) 
            : 0;
          const newWordId = maxWordId + 1;
          
          const newWord = {
            wordId: newWordId,
            text: newWordText,
            boundingBox: newWordBoundingBox,
            confidence: 100
          };
          
          const updatedWords = [...block.words, newWord];
          
          // Recalculate block bounding box
          const newBlockBounds = recalculateBlockBounds(updatedWords);
          
          return {
            ...block,
            words: updatedWords,
            ...(newBlockBounds && { boundingBox: newBlockBounds })
          };
        }
        return block;
      });
      
      const updatedAddBlocksByPage = new Map(state.blocksByPage);
      updatedAddBlocksByPage.set(addPageIndex, updatedAddBlocks);
      
      return {
        ...state,
        blocksByPage: updatedAddBlocksByPage,
      };
      
    case 'REORDER_WORD_IN_BLOCK':
      const { pageIndex: reorderPageIndex, blockId: reorderBlockId, wordId: reorderWordId, direction } = action.payload;
      const reorderBlocks = state.blocksByPage.get(reorderPageIndex) || [];
      const updatedReorderBlocks = reorderBlocks.map(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        if (blockIdNum === reorderBlockId) {
          const wordIndex = block.words.findIndex(w => w.wordId === reorderWordId);
          if (wordIndex === -1) return block;
          
          const newWords = [...block.words];
          let swapIndex = -1;
          
          if (direction === 'up' && wordIndex > 0) {
            swapIndex = wordIndex - 1;
          } else if (direction === 'down' && wordIndex < newWords.length - 1) {
            swapIndex = wordIndex + 1;
          }
          
          if (swapIndex !== -1) {
            // Swap the words
            [newWords[wordIndex], newWords[swapIndex]] = [newWords[swapIndex], newWords[wordIndex]];
            
            return {
              ...block,
              words: newWords
            };
          }
        }
        return block;
      });
      
      const updatedReorderBlocksByPage = new Map(state.blocksByPage);
      updatedReorderBlocksByPage.set(reorderPageIndex, updatedReorderBlocks);
      
      return {
        ...state,
        blocksByPage: updatedReorderBlocksByPage,
      };
      
    case 'DELETE_WORD_FROM_BLOCK':
      const { pageIndex: deletePageIndex, blockId: deleteWordBlockId, wordId: deleteWordId } = action.payload;
      const deleteBlocks = state.blocksByPage.get(deletePageIndex) || [];
      
      // Check if the block has only one word - if so, delete the entire block
      const blockToDelete = deleteBlocks.find(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        return blockIdNum === deleteWordBlockId;
      });
      
      if (blockToDelete && blockToDelete.words.length === 1) {
        // Delete the entire block since it only has one word
        const filteredBlocks = deleteBlocks.filter(block => {
          const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
          return blockIdNum !== deleteWordBlockId;
        });
        
        const updatedDeleteBlocksByPage = new Map(state.blocksByPage);
        updatedDeleteBlocksByPage.set(deletePageIndex, filteredBlocks);
        
        return {
          ...state,
          blocksByPage: updatedDeleteBlocksByPage,
        };
      }
      
      // Otherwise, just delete the word from the block
      const updatedDeleteBlocks = deleteBlocks.map(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        if (blockIdNum === deleteWordBlockId) {
          const filteredWords = block.words.filter(w => w.wordId !== deleteWordId);
          
          // Recalculate block bounding box (same as when moving words)
          const newBlockBounds = recalculateBlockBounds(filteredWords);
          
          return {
            ...block,
            words: filteredWords,
            boundingBox: newBlockBounds || { x: 0, y: 0, width: 0, height: 0 }
          };
        }
        return block;
      });
      
      const updatedDeleteBlocksByPage = new Map(state.blocksByPage);
      updatedDeleteBlocksByPage.set(deletePageIndex, updatedDeleteBlocks);
      
      return {
        ...state,
        blocksByPage: updatedDeleteBlocksByPage,
      };
      
    case 'UPDATE_BLOCK_METADATA':
      const { pageIndex: updatePageIndex, blockId: updateBlockId, metadata } = action.payload;
      const updateBlocks = state.blocksByPage.get(updatePageIndex) || [];
      const updatedMetadataBlocks = updateBlocks.map(block => {
        const blockIdNum = typeof block.blockId === 'string' ? parseInt(block.blockId, 10) : block.blockId;
        if (blockIdNum === updateBlockId) {
          return {
            ...block,
            ...metadata,
          };
        }
        return block;
      });
      
      const updatedMetadataBlocksByPage = new Map(state.blocksByPage);
      updatedMetadataBlocksByPage.set(updatePageIndex, updatedMetadataBlocks);
      
      return {
        ...state,
        blocksByPage: updatedMetadataBlocksByPage,
      };
      
    case 'RECALCULATE_OVERLAYS':
      return {
        ...state,
        recalcTrigger: Date.now(),
      };
      
    case 'CLEAR_WORD_HIGHLIGHTS':
      return {
        ...state,
        highlightedWords: new Set(),
      };
      
    case 'CLEAR_SELECTION':
      return {
        ...state,
        selectedWord: null,
        highlightedWords: new Set(),
        editMode: null,
      };
      
    case 'RESET_STATE':
      return initialState;
      
    default:
      return state;
  }
}

// === Context ===
interface WordDataContextType {
  state: WordDataState;
  actions: {
    setActiveSpread: (leftPageIndex: number | null, rightPageIndex: number | null) => void;
    loadPageBlocks: (pageIndex: number, isbn: string, wordsFilename: string, forceReload?: boolean) => Promise<void>;
    updatePageBlocks: (pageIndex: number, blocks: BlockData[]) => void;
    updateWordText: (pageIndex: number, blockId: number, wordId: string | number, text: string) => void;
    updateWordPosition: (pageIndex: number, blockId: number, wordId: string | number, boundingBox: { x: number; y: number; width: number; height: number }) => void;
    addWordToBlock: (pageIndex: number, blockId: number, text: string, boundingBox: { x: number; y: number; width: number; height: number }) => void;
    reorderWordInBlock: (pageIndex: number, blockId: number, wordId: string | number, direction: 'up' | 'down') => void;
    deleteWordFromBlock: (pageIndex: number, blockId: number, wordId: string | number) => void;
    deleteBlock: (pageIndex: number, blockId: number) => void;
    updateBlockMetadata: (pageIndex: number, blockId: number, metadata: Partial<BlockData>) => void;
    setSelectedWord: (wordId: string | null, pageIndex?: number) => void;
    setFlashingWord: (wordId: string | null, pageIndex?: number) => void;
    toggleWordHighlight: (wordId: string) => void;
    setHighlightedBlock: (block: { blockId: number; boundingBox: { x: number; y: number; width: number; height: number } } | null) => void;
    setShowWords: (show: boolean) => void;
    setEditMode: (mode: 'text' | 'resize' | 'move' | 'delete-word' | 'delete-block' | null) => void;
    recalculateOverlays: () => void;
    clearSelection: () => void;
    resetState: () => void;
  };
}

const WordDataContext = createContext<WordDataContextType | undefined>(undefined);

// === Provider ===
interface WordDataProviderProps {
  children: ReactNode;
}

export const WordDataProvider: React.FC<WordDataProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(wordDataReducer, initialState);
  const loadedPagesRef = useRef<Set<number>>(new Set());
  
  // Memoized actions to prevent infinite re-renders
  const setActiveSpread = useCallback((leftPageIndex: number | null, rightPageIndex: number | null) => {
    dispatch({ type: 'SET_ACTIVE_SPREAD', payload: { leftPageIndex, rightPageIndex } });
  }, []);
  
  const loadPageBlocks = useCallback(async (pageIndex: number, isbn: string, wordsFilename: string, forceReload: boolean = false) => {
    if (!wordsFilename) return;
    
    // Don't reload if blocks already exist for this page (unless forceReload is true)
    // This prevents overwriting unsaved changes when clicking outside blocks
    if (!forceReload && loadedPagesRef.current.has(pageIndex)) {
      return; // Already loaded, don't overwrite
    }
    
    dispatch({ type: 'SET_PAGE_LOADING', payload: { pageIndex, loading: true } });
    
    try {
      const bookDataService = createBookDataService(isbn);
      const wordData = await bookDataService.loadWordData(wordsFilename);
      
      // Log file path if available in response
      if (wordData && typeof wordData === 'object' && (wordData as any)._filePath) {
        console.log(`[ID COLLISION - LOAD] File path on disk: ${(wordData as any)._filePath}`);
        // Remove the metadata from the data object
        delete (wordData as any)._filePath;
      }
      
      // Log all page data before collision detection
      if (wordData.blocks && Array.isArray(wordData.blocks)) {
        console.log(`[ID COLLISION - LOAD] Page index ${pageIndex}, words filename: ${wordsFilename} - Source data analysis:`);
        console.log(`[ID COLLISION - LOAD] Total blocks: ${wordData.blocks.length}`);
        
        // Collect all wordIds to detect duplicates
        const allWordIds: (string | number)[] = [];
        wordData.blocks.forEach((block: any, blockIndex: number) => {
          const blockId = block.blockId || `block-${blockIndex}`;
          const wordCount = block.words && Array.isArray(block.words) ? block.words.length : 0;
          const wordIds = block.words && Array.isArray(block.words) 
            ? block.words.map((w: any) => w.wordId)
            : [];
          
          // Check for duplicates within this block
          const blockWordIds = new Set<string | number>();
          const duplicatesInBlock: (string | number)[] = [];
          wordIds.forEach((id: string | number) => {
            if (blockWordIds.has(id)) {
              duplicatesInBlock.push(id);
            } else {
              blockWordIds.add(id);
            }
            allWordIds.push(id);
          });
          
          const wordIdsStr = wordIds.join(', ');
          const duplicatesStr = duplicatesInBlock.length > 0 ? ` ⚠️ DUPLICATES IN BLOCK: [${duplicatesInBlock.join(', ')}]` : '';
          console.log(`[ID COLLISION - LOAD]   Block ${blockIndex}: blockId=${blockId}, words=${wordCount}, wordIds=[${wordIdsStr}]${duplicatesStr}`);
        });
        
        // Check for duplicates across all blocks
        const allWordIdsSet = new Set<string | number>();
        const duplicatesAcrossBlocks: (string | number)[] = [];
        allWordIds.forEach((id: string | number) => {
          if (allWordIdsSet.has(id)) {
            duplicatesAcrossBlocks.push(id);
          } else {
            allWordIdsSet.add(id);
          }
        });
        
        if (duplicatesAcrossBlocks.length > 0) {
          console.log(`[ID COLLISION - LOAD] ⚠️ DUPLICATE WORD IDs ACROSS ALL BLOCKS: [${duplicatesAcrossBlocks.join(', ')}]`);
        }
      }
      
      // Extract blocks from word data
      let blocks: BlockData[] = [];
      
      if (wordData.blocks && Array.isArray(wordData.blocks)) {
        // Check for collisions WITHIN this page only (not across pages)
        // Track adjusted IDs as we process blocks/words
        const processedBlockIds = new Set<string | number>();
        const processedWordIds = new Set<string | number>();
        
        // Direct blocks array (new format)
        blocks = wordData.blocks.map((block: any, index: number) => {
          const originalBlockId = block.blockId || `block-${index}`;
          
          // Use new collision handler - only check within this page (against already processed blocks)
          const blockIdResult = adjustIdIfCollision(originalBlockId, processedBlockIds);
          const adjustedBlockId = blockIdResult.adjustedId;
          
          // Log if block ID was adjusted
          if (blockIdResult.wasAdjusted) {
            console.log(`[ID COLLISION - LOAD] Block ID collision detected: original=${originalBlockId}, new=${adjustedBlockId}`);
          }
          
          // Track both original and adjusted block IDs
          processedBlockIds.add(originalBlockId);
          if (blockIdResult.wasAdjusted) {
            processedBlockIds.add(adjustedBlockId);
          }
          
          return {
            blockId: adjustedBlockId,
            blockType: block.blockType,
            boundingBox: block.boundingBox,
            words: (block.words || []).map((word: any, wordIndex: number) => {
              const originalWordId = word.wordId;
              
              // Use new collision handler - only check within this page (against already processed words)
              const wordIdResult = adjustIdIfCollision(originalWordId, processedWordIds);
              const adjustedWordId = wordIdResult.adjustedId;
              
              // Log if word ID was adjusted
              if (wordIdResult.wasAdjusted) {
                console.log(`[ID COLLISION - LOAD] Word ID collision detected: original=${originalWordId}, new=${adjustedWordId}`);
              }
              
              // Track both original and adjusted word IDs
              processedWordIds.add(originalWordId);
              if (wordIdResult.wasAdjusted) {
                processedWordIds.add(adjustedWordId);
              }
            const originalBoundingBox = word.boundingBox;
            const normalizedBoundingBox = { ...originalBoundingBox };
            
            // Normalize width: if 0 change to 150
            if (normalizedBoundingBox.width === 0) {
              normalizedBoundingBox.width = 150;
            }
            
            // Normalize height: if 0 change to 50
            if (normalizedBoundingBox.height === 0) {
              normalizedBoundingBox.height = 50;
            }
            
            // Store original values if they were normalized
            const needsOriginal = 
              originalBoundingBox.width === 0 ||
              originalBoundingBox.height === 0;
            
              return {
                ...word,
                wordId: adjustedWordId,
                originalWordId: originalWordId, // Store original for saving
                boundingBox: normalizedBoundingBox,
                ...(needsOriginal && { originalBoundingBox })
              };
            }),
            originalBlockId: originalBlockId, // Store original for saving
            readBlock: block.readBlock !== undefined ? block.readBlock : true,
            blockReadOrder: block.blockReadOrder || index + 1,
            'ai-confidence': 0,
            possibleIssue: '-',
            originalBlockData: block
          };
        });
      } else {
        // No enhanced JSON format found
        blocks = [];
      }
      
      dispatch({ type: 'LOAD_PAGE_BLOCKS', payload: { pageIndex, wordData, blocks } });
      loadedPagesRef.current.add(pageIndex);
      
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Failed to load word data' });
      dispatch({ type: 'SET_PAGE_LOADING', payload: { pageIndex, loading: false } });
    }
  }, []);
  
  const updatePageBlocks = useCallback((pageIndex: number, blocks: BlockData[]) => {
    dispatch({ type: 'UPDATE_PAGE_BLOCKS', payload: { pageIndex, blocks } });
  }, []);
  
  const setEditMode = useCallback((mode: 'text' | 'resize' | 'move' | 'delete-word' | 'delete-block' | null) => {
    dispatch({ type: 'SET_EDIT_MODE', payload: mode });
  }, []);
  
  const deleteBlock = useCallback((pageIndex: number, blockId: number) => {
    dispatch({ type: 'DELETE_BLOCK', payload: { pageIndex, blockId } });
  }, []);
  
  const setSelectedWord = useCallback((wordId: string | null, pageIndex?: number) => {
    if (wordId === null) {
      console.log('[WordDataContext] Clearing selected word');
      dispatch({ type: 'SET_SELECTED_WORD', payload: null });
    } else if (pageIndex !== undefined) {
      console.log('[WordDataContext] Setting selected word:', { wordId, pageIndex });
      dispatch({ type: 'SET_SELECTED_WORD', payload: { wordId, pageIndex } });
    } else {
      // Fallback for backward compatibility - clear selection if pageIndex not provided
      console.log('[WordDataContext] pageIndex not provided, clearing selection');
      dispatch({ type: 'SET_SELECTED_WORD', payload: null });
    }
  }, []);
  
  const setFlashingWord = useCallback((wordId: string | null, pageIndex?: number) => {
    if (wordId === null) {
      dispatch({ type: 'SET_FLASHING_WORD', payload: null });
    } else if (pageIndex !== undefined) {
      dispatch({ type: 'SET_FLASHING_WORD', payload: { wordId, pageIndex } });
    } else {
      dispatch({ type: 'SET_FLASHING_WORD', payload: null });
    }
  }, []);
  
  const toggleWordHighlight = useCallback((wordId: string) => {
    dispatch({ type: 'TOGGLE_WORD_HIGHLIGHT', payload: wordId });
  }, []);
  
  const setHighlightedBlock = useCallback((block: { blockId: number; boundingBox: { x: number; y: number; width: number; height: number } } | null) => {
    dispatch({ type: 'SET_HIGHLIGHTED_BLOCK', payload: block });
  }, []);
  
  const setShowWords = useCallback((show: boolean) => {
    dispatch({ type: 'SET_SHOW_WORDS', payload: show });
  }, []);
  
  const recalculateOverlays = useCallback(() => {
    dispatch({ type: 'RECALCULATE_OVERLAYS' });
  }, []);
  
  const clearSelection = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTION' });
  }, []);
  
  const resetState = useCallback(() => {
    loadedPagesRef.current.clear();
    dispatch({ type: 'RESET_STATE' });
  }, []);
  
  const updateWordText = useCallback((pageIndex: number, blockId: number, wordId: string | number, text: string) => {
    dispatch({ 
      type: 'UPDATE_WORD_TEXT', 
      payload: { pageIndex, blockId, wordId: wordId as string | number, text } 
    });
  }, []);
  
  const updateWordPosition = useCallback((pageIndex: number, blockId: number, wordId: string | number, boundingBox: { x: number; y: number; width: number; height: number }) => {
    dispatch({ 
      type: 'UPDATE_WORD_POSITION', 
      payload: { pageIndex, blockId, wordId: wordId as string | number, boundingBox } 
    });
  }, []);
  
  const addWordToBlock = useCallback((pageIndex: number, blockId: number, text: string, boundingBox: { x: number; y: number; width: number; height: number }) => {
    dispatch({ 
      type: 'ADD_WORD_TO_BLOCK', 
      payload: { pageIndex, blockId, text, boundingBox } 
    });
  }, []);
  
  const reorderWordInBlock = useCallback((pageIndex: number, blockId: number, wordId: string | number, direction: 'up' | 'down') => {
    dispatch({ 
      type: 'REORDER_WORD_IN_BLOCK', 
      payload: { pageIndex, blockId, wordId, direction } 
    });
  }, []);
  
  const deleteWordFromBlock = useCallback((pageIndex: number, blockId: number, wordId: string | number) => {
    dispatch({ 
      type: 'DELETE_WORD_FROM_BLOCK', 
      payload: { pageIndex, blockId, wordId } 
    });
  }, []);
  
  const updateBlockMetadata = useCallback((pageIndex: number, blockId: number, metadata: Partial<BlockData>) => {
    dispatch({ 
      type: 'UPDATE_BLOCK_METADATA', 
      payload: { pageIndex, blockId, metadata } 
    });
  }, []);
  
  // Memoize the actions to prevent unnecessary re-renders
  const actions = useMemo(() => ({
    setActiveSpread,
    loadPageBlocks,
    updatePageBlocks,
    updateWordText,
    updateWordPosition,
    addWordToBlock,
    reorderWordInBlock,
    deleteWordFromBlock,
    deleteBlock,
    updateBlockMetadata,
    setSelectedWord,
    setFlashingWord,
    toggleWordHighlight,
    setHighlightedBlock,
    setShowWords,
    setEditMode,
    recalculateOverlays,
    clearSelection,
    resetState,
  }), [
    setActiveSpread,
    loadPageBlocks,
    updatePageBlocks,
    updateWordText,
    updateWordPosition,
    addWordToBlock,
    reorderWordInBlock,
    deleteWordFromBlock,
    deleteBlock,
    updateBlockMetadata,
    setSelectedWord,
    setFlashingWord,
    toggleWordHighlight,
    setHighlightedBlock,
    setShowWords,
    setEditMode,
    recalculateOverlays,
    clearSelection,
    resetState,
  ]);
  
  // ESC key handler to exit edit mode
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && state.editMode !== null) {
        setEditMode(null);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.editMode, setEditMode]);

  // Create context value with stable actions but reactive state
  const contextValue: WordDataContextType = useMemo(() => ({
    state,
    actions,
  }), [state, actions]);
  
  return (
    <WordDataContext.Provider value={contextValue}>
      {children}
    </WordDataContext.Provider>
  );
};

// === Hook ===
export const useWordDataContext = (): WordDataContextType => {
  const context = useContext(WordDataContext);
  if (context === undefined) {
    throw new Error('useWordDataContext must be used within a WordDataProvider');
  }
  return context;
};

export default WordDataContext;
